// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  airbornePhaseNames,
  groundPhaseNames,
  patternWorkCandidatePhases,
  patternWorkPhaseName,
} from "../core/config.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
} from "../core/schema.js";
import {
  clampScore,
  finiteValues,
  quantile,
} from "./utils.js";

function sampleIntervalSeconds(points) {
  const diffs = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1].elapsedSeconds;
    const current = points[index].elapsedSeconds;
    if (Number.isFinite(previous) && Number.isFinite(current) && current > previous) {
      diffs.push(current - previous);
    }
  }
  return quantile(diffs, 0.5) ?? 1;
}

function rollingAverage(values, radius) {
  return values.map((_, index) => {
    let total = 0;
    let count = 0;
    const start = Math.max(0, index - radius);
    const end = Math.min(values.length - 1, index + radius);
    for (let cursor = start; cursor <= end; cursor += 1) {
      if (!Number.isFinite(values[cursor])) continue;
      total += values[cursor];
      count += 1;
    }
    return count ? total / count : null;
  });
}

function normalizedSignal(value, low, high) {
  if (!Number.isFinite(value) || !Number.isFinite(low) || !Number.isFinite(high) || high <= low) return null;
  return Math.max(0, Math.min(1, (value - low) / (high - low)));
}

function median(values) {
  return quantile(values, 0.5);
}

function rangeOf(values) {
  const finite = values.filter(Number.isFinite);
  if (!finite.length) return null;
  return Math.max(...finite) - Math.min(...finite);
}

function phaseChannelScale(points, key) {
  const values = finiteValues(points, key).filter((value) => value > 0);
  return {
    low: quantile(values, 0.12),
    high: quantile(values, 0.92),
  };
}

function phaseChannelCoverage(points, key) {
  return finiteValues(points, key).length / Math.max(points.length, 1);
}

function flagRuns(flags) {
  const runs = [];
  let start = 0;
  while (start < flags.length) {
    let end = start + 1;
    while (end < flags.length && flags[end] === flags[start]) end += 1;
    runs.push({ start, end, value: flags[start], count: end - start });
    start = end;
  }
  return runs;
}

function runDurationSeconds(points, run) {
  const first = points[run.start];
  const last = points[Math.max(run.start, run.end - 1)];
  const startTime = first?.elapsedSeconds ?? run.start;
  const endTime = last?.elapsedSeconds ?? Math.max(run.start, run.end - 1);
  const duration = endTime - startTime;
  return Number.isFinite(duration) ? Math.max(0, duration) : Math.max(0, run.end - run.start);
}

function phasePointHasEngineRunEvidence(point, index, context = {}) {
  const rpmKnown = Number.isFinite(point?.rpm);
  const fuelKnown = Number.isFinite(point?.fuelFlow);
  const rpmRunning = rpmKnown && point.rpm > 500;
  const fuelRunning = fuelKnown && point.fuelFlow > 0.8;
  if (rpmKnown || fuelKnown) return rpmRunning || fuelRunning;

  const egtHot = Number.isFinite(point?.egt) && point.egt > 850;
  if (egtHot) return true;

  const smoothedPower = context.smoothedPower?.[index];
  if (!context.hasDirectPowerCoverage && Number.isFinite(smoothedPower) && smoothedPower > 0.18) return true;

  const chtWarm = Number.isFinite(point?.cht) && point.cht > 260;
  return !context.hasDirectPowerCoverage && !context.hasEgtCoverage && chtWarm;
}

function phaseEngineRunFlags(points, smoothedPower = []) {
  if (!points.length) return [];
  const interval = sampleIntervalSeconds(points);
  const hasDirectPowerCoverage = phaseChannelCoverage(points, "rpm") >= 0.08
    || phaseChannelCoverage(points, "fuelFlow") >= 0.08;
  const hasEgtCoverage = phaseChannelCoverage(points, "egt") >= 0.08;
  const context = { smoothedPower, hasDirectPowerCoverage, hasEgtCoverage };
  const flags = points.map((point, index) => phasePointHasEngineRunEvidence(point, index, context));
  const closeGapSamples = Math.max(2, Math.round(35 / Math.max(interval, 0.25)));
  const minRunSamples = Math.max(2, Math.round(8 / Math.max(interval, 0.25)));

  for (const run of flagRuns(flags)) {
    if (run.value || run.start === 0 || run.end >= flags.length || run.count > closeGapSamples) continue;
    for (let index = run.start; index < run.end; index += 1) flags[index] = true;
  }

  for (const run of flagRuns(flags)) {
    if (!run.value || run.count >= minRunSamples) continue;
    for (let index = run.start; index < run.end; index += 1) flags[index] = false;
  }

  return flags;
}

function phaseEngineRunSegments(points, flags = phaseEngineRunFlags(points), interval = sampleIntervalSeconds(points)) {
  const minimumDuration = Math.max(45, interval * 6);
  return flagRuns(flags)
    .filter((run) => run.value && runDurationSeconds(points, run) >= minimumDuration);
}

function phaseSegmentWindows(points, runSegments, interval) {
  const preSamples = Math.max(1, Math.round(150 / Math.max(interval, 0.25)));
  const postSamples = Math.max(1, Math.round(90 / Math.max(interval, 0.25)));
  const windows = runSegments.map((run, index) => {
    let start = Math.max(0, run.start - preSamples);
    let end = Math.min(points.length, run.end + postSamples);
    const previous = runSegments[index - 1];
    const next = runSegments[index + 1];
    if (previous) {
      const midpoint = previous.end + Math.floor((run.start - previous.end) / 2);
      start = Math.max(start, midpoint);
    }
    if (next) {
      const midpoint = run.end + Math.floor((next.start - run.end) / 2);
      end = Math.min(end, midpoint);
    }
    return { start, end, run };
  });

  for (let index = 1; index < windows.length; index += 1) {
    const previous = windows[index - 1];
    const current = windows[index];
    if (previous.end <= current.start) continue;
    const split = Math.max(previous.start + 1, Math.min(current.end - 1, Math.floor((previous.run.end + current.run.start) / 2)));
    previous.end = Math.min(previous.end, split);
    current.start = Math.max(current.start, split);
  }

  return windows.filter((window) => window.end > window.start);
}

function phaseOffGapPoint(point, index, windows) {
  const beforeFirstRun = windows.length && index < windows[0].start;
  return {
    ...point,
    phase: beforeFirstRun ? "Startup" : "Shutdown",
    phaseConfidence: "High",
    phaseEvidence: beforeFirstRun ? "before engine-run segment" : "engine-off gap between runs",
    phaseSource: "engine run segmentation",
  };
}

function assignMultiRunFlightPhases(points, runSegments, interval) {
  const windows = phaseSegmentWindows(points, runSegments, interval);
  const assigned = Array(points.length).fill(null);

  windows.forEach((window) => {
    const segment = points.slice(window.start, window.end);
    assignSingleFlightPhases(segment).forEach((point, offset) => {
      assigned[window.start + offset] = point;
    });
  });

  return points.map((point, index) => assigned[index] ?? phaseOffGapPoint(point, index, windows));
}

function sustainedIndex(flags, startIndex = 0, sampleInterval = 1, seconds = 20) {
  const needed = Math.max(2, Math.round(seconds / Math.max(sampleInterval, 0.25)));
  let streak = 0;
  for (let index = Math.max(0, startIndex); index < flags.length; index += 1) {
    streak = flags[index] ? streak + 1 : 0;
    if (streak >= needed) return index - streak + 1;
  }
  return -1;
}

function sustainedRunCount(flags, sampleInterval = 1, seconds = 20) {
  const needed = Math.max(2, Math.round(seconds / Math.max(sampleInterval, 0.25)));
  let count = 0;
  let streak = 0;
  flags.forEach((flag) => {
    if (!flag) {
      streak = 0;
      return;
    }
    streak += 1;
    if (streak === needed) count += 1;
  });
  return count;
}

function phaseChannelAbsMax(points, key) {
  const values = finiteValues(points, key).map((value) => Math.abs(value));
  return values.length ? Math.max(...values) : null;
}

function phaseChannelAbsentOrNearZero(points, key, tolerance) {
  const values = finiteValues(points, key);
  if (!values.length) return true;
  const absMax = Math.max(...values.map((value) => Math.abs(value)));
  return Number.isFinite(absMax) && absMax <= tolerance;
}

function phaseGpsMotionLooksPlaceholder(points) {
  const speedValues = finiteValues(points, "groundSpeed");
  const speedAbsMax = phaseChannelAbsMax(points, "groundSpeed");
  return speedValues.length >= points.length * 0.35
    && Number.isFinite(speedAbsMax)
    && speedAbsMax <= 1
    && phaseChannelAbsentOrNearZero(points, "altitude", 50)
    && phaseChannelAbsentOrNearZero(points, "gpsAltitude", 50)
    && phaseChannelAbsentOrNearZero(points, "latitude", 0.01)
    && phaseChannelAbsentOrNearZero(points, "longitude", 0.01)
    && phaseChannelAbsentOrNearZero(points, "gpsTrack", 1);
}

function bestPhaseSpeedKey(points, ignoredKeys = new Set()) {
  return ["ias", "tas", "groundSpeed"].find((key) => !ignoredKeys.has(key) && finiteValues(points, key).length >= points.length * 0.35) ?? null;
}

function phaseGroundContactValues(points) {
  return points.map((point) => {
    if (typeof point?.onGround === "boolean") return point.onGround;
    if (typeof point?.weightOnWheels === "boolean") return point.weightOnWheels;
    return null;
  });
}

function phaseGroundContactCoverage(values) {
  return values.filter((value) => typeof value === "boolean").length / Math.max(values.length, 1);
}

function deriveVerticalSpeeds(points, smoothedAltitude) {
  return points.map((point, index) => {
    if (Number.isFinite(point.verticalSpeed)) return point.verticalSpeed;
    if (index === 0 || !Number.isFinite(smoothedAltitude[index]) || !Number.isFinite(smoothedAltitude[index - 1])) return null;
    const current = point.elapsedSeconds;
    const previous = points[index - 1].elapsedSeconds;
    const seconds = Number.isFinite(current) && Number.isFinite(previous) ? current - previous : 1;
    if (seconds <= 0) return null;
    return (smoothedAltitude[index] - smoothedAltitude[index - 1]) / seconds * 60;
  });
}

function phaseAltitudeInput(points, speedKey, { ignoreGpsPlaceholder = false } = {}) {
  return points.map((point) => {
    if (ignoreGpsPlaceholder) return null;
    const altitude = point.altitude;
    if (!Number.isFinite(altitude)) return null;
    const speed = speedKey ? point[speedKey] : null;
    if (Number.isFinite(speed)) {
      if (speed < 5 && altitude > 1000) return null;
      if (speed > 35 && altitude < 100) return null;
    }
    return altitude;
  });
}

function phaseDataBasis(points, speedKey, groundContactValues = phaseGroundContactValues(points), { ignoreGpsPlaceholder = false } = {}) {
  const gpsPlaceholderKeys = new Set(["groundSpeed", "altitude", "gpsAltitude", "latitude", "longitude", "gpsTrack"]);
  const coverage = (key) => ignoreGpsPlaceholder && gpsPlaceholderKeys.has(key)
    ? 0
    : finiteValues(points, key).length / Math.max(points.length, 1);
  const powerChannels = ["fuelFlow", "rpm", "map"].filter((key) => coverage(key) >= 0.35);
  const hasAltitude = coverage("altitude") >= 0.35;
  const hasVerticalSpeed = coverage("verticalSpeed") >= 0.35;
  const hasGroundContactData = phaseGroundContactCoverage(groundContactValues) >= 0.35;
  const hasMotionData = Boolean(speedKey) || hasAltitude || hasVerticalSpeed || hasGroundContactData;
  return {
    hasMotionData,
    hasAltitude,
    hasVerticalSpeed,
    hasGroundContactData,
    powerChannels,
    sourceLabel: Boolean(speedKey) || hasAltitude || hasVerticalSpeed
      ? "air/GPS + engine"
      : hasGroundContactData
        ? "ground-contact + engine"
        : powerChannels.length >= 1
        ? "engine data only"
        : "thermal trend only",
  };
}

function confidenceRank(confidence) {
  return { Low: 0, Medium: 1, High: 2 }[confidence] ?? 0;
}

function confidenceFromRank(rank) {
  if (rank >= 2) return "High";
  if (rank >= 1) return "Medium";
  return "Low";
}

function formatPhaseValue(value, suffix = "", digits = 0) {
  return Number.isFinite(value) ? `${value.toFixed(digits)}${suffix}` : null;
}

function compactPhaseEvidence(parts) {
  return parts.filter(Boolean).slice(0, 4).join(", ");
}

function phasePointMetadata(point, index, context) {
  const label = point.phase;
  const power = context.smoothedPower[index];
  const speed = context.speedValues[index];
  const vs = context.verticalSpeeds[index];
  const altitude = context.altitudeValues[index];
  const speedText = context.speedKey ? formatPhaseValue(speed, " kt") : null;
  const groundContact = context.groundContactValues?.[index];
  const groundContactText = typeof groundContact === "boolean"
    ? groundContact ? "ground-contact true" : "ground-contact false"
    : null;
  const vsText = formatPhaseValue(vs, " fpm");
  const altitudeText = formatPhaseValue(altitude, " ft");
  const fuelText = formatPhaseValue(point.fuelFlow, " GPH", 1);
  const rpmText = formatPhaseValue(point.rpm, " RPM");
  const mapText = formatPhaseValue(point.map, " inHg", 1);
  const powerText = Number.isFinite(power) ? `${Math.round(power * 100)}% power score` : null;
  const hasMotion = context.basis.hasMotionData;
  const hasEnginePower = context.basis.powerChannels.length >= 1;
  const hasThermal = Number.isFinite(point.egt) || Number.isFinite(point.cht);
  const movingEvidence = Number.isFinite(speed) || Number.isFinite(vs) || Number.isFinite(altitude);
  let confidence = "Medium";
  let evidence = "";

  if (["Startup", "Shutdown"].includes(label)) {
    confidence = hasEnginePower || hasThermal ? "High" : "Medium";
    evidence = compactPhaseEvidence([context.basis.sourceLabel, fuelText, rpmText, powerText]);
  } else if (label === "Taxi") {
    confidence = hasMotion ? "High" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([groundContactText, hasMotion ? `${context.speedKey ?? "motion"} low` : "before takeoff-like power", speedText, fuelText, rpmText]);
  } else if (label === "Runup") {
    confidence = context.basis.hasGroundContactData ? "High" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([groundContactText, context.likelyGroundTest ? "engine-only ground runup" : "pre-takeoff high power", fuelText, rpmText, mapText]);
  } else if (label === "Ground Run") {
    confidence = context.likelyGroundTest || hasMotion ? "High" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([groundContactText, context.likelyGroundTest ? "no airborne evidence" : "high-power ground run", speedText, fuelText, rpmText]);
  } else if (label === "Takeoff") {
    confidence = context.firstAirborne >= 0 && context.firstHighPower >= 0 ? "High" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([groundContactText, context.firstAirborne >= 0 ? "airborne transition" : "takeoff inferred", speedText, fuelText, rpmText]);
  } else if (label === "Climb") {
    confidence = Number.isFinite(vs) && vs > 180 ? "High" : hasMotion ? "Medium" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([Number.isFinite(vs) ? `VS ${Math.round(vs)} fpm` : "post-takeoff high power", speedText, fuelText, mapText]);
  } else if (label === patternWorkPhaseName) {
    const cycles = point.patternWorkCycleCount;
    const score = point.patternWorkScore;
    confidence = Number.isFinite(score) && score >= 78 ? "High" : "Medium";
    evidence = compactPhaseEvidence([
      Number.isFinite(cycles) ? `${cycles} power/thermal cycles` : "repeating power/thermal cycles",
      Number.isFinite(score) ? `${Math.round(score)}% pattern score` : null,
      fuelText,
      mapText,
    ]);
  } else if (label === "Cruise") {
    confidence = movingEvidence && Math.abs(vs ?? 0) < 180 ? "High" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([hasMotion ? "stable enroute segment" : "stable engine segment", speedText, altitudeText, fuelText]);
  } else if (label === "Descent") {
    confidence = Number.isFinite(vs) && vs < -180 ? "High" : hasMotion ? "Medium" : hasEnginePower ? "Medium" : "Low";
    evidence = compactPhaseEvidence([Number.isFinite(vs) ? `VS ${Math.round(vs)} fpm` : "late power reduction", speedText, fuelText, mapText]);
  } else if (label === "Landing") {
    confidence = hasMotion ? "Medium" : hasEnginePower ? "Low" : "Low";
    evidence = compactPhaseEvidence([groundContactText, hasMotion ? "end of airborne segment" : "landing inferred", speedText, fuelText, rpmText]);
  } else {
    confidence = "Low";
    evidence = context.basis.sourceLabel;
  }

  if (!hasMotion && airbornePhaseNames.has(label)) {
    confidence = "Low";
    evidence = `${evidence}; inferred from engine data only`;
  }

  if (context.logStartedInFlight && index <= (context.logStartedInFlightEvidenceEndIndex ?? 0) && airbornePhaseNames.has(label)) {
    confidence = confidenceFromRank(Math.max(confidenceRank(confidence), 1));
    evidence = compactPhaseEvidence(["log began hot/in flight", evidence]);
  }

  return {
    phaseConfidence: confidence,
    phaseEvidence: evidence,
    phaseSource: context.basis.sourceLabel,
  };
}

function addPhaseMetadata(points, context) {
  return points.map((point, index) => ({
    ...point,
    ...phasePointMetadata(point, index, context),
  }));
}

function phaseThermalAverage(point, prefix) {
  const values = cylinderKeys(prefix).map((key) => point?.[key]).filter(Number.isFinite);
  if (values.length) return averageValues(values);
  return Number.isFinite(point?.[prefix]) ? point[prefix] : null;
}

function phaseRangeQuantile(values, start, end, q) {
  return quantile(values.slice(Math.max(0, start), Math.min(values.length, end)).filter(Number.isFinite), q);
}

function phaseRunMedian(values, run) {
  return phaseRangeQuantile(values, run.start, run.end, 0.5);
}

function phaseRunCenterSeconds(points, run) {
  const start = points[run.start]?.elapsedSeconds ?? run.start;
  const end = points[Math.max(run.start, run.end - 1)]?.elapsedSeconds ?? Math.max(run.start, run.end - 1);
  return (start + end) / 2;
}

function cleanPhasePatternFlags(flags, closeGapSamples, minRunSamples) {
  const cleaned = [...flags];
  for (const run of flagRuns(cleaned)) {
    if (run.value || run.start === 0 || run.end >= cleaned.length || run.count > closeGapSamples) continue;
    for (let index = run.start; index < run.end; index += 1) cleaned[index] = true;
  }
  for (const run of flagRuns(cleaned)) {
    if (!run.value || run.count >= minRunSamples) continue;
    for (let index = run.start; index < run.end; index += 1) cleaned[index] = false;
  }
  return cleaned;
}

function patternWorkPowerProxy(points, context) {
  const directPowerAvailable = (context.basis?.powerChannels?.length ?? 0) > 0;
  const interval = context.interval ?? sampleIntervalSeconds(points);
  const radius = Math.max(1, Math.round((directPowerAvailable ? 12 : 18) / Math.max(interval, 0.25)));
  if (directPowerAvailable) return rollingAverage(context.smoothedPower ?? [], radius);

  const egtValues = points.map((point) => phaseThermalAverage(point, "egt"));
  const scale = {
    low: quantile(egtValues, 0.12),
    high: quantile(egtValues, 0.92),
  };
  return rollingAverage(egtValues.map((value) => normalizedSignal(value, scale.low, scale.high)), radius);
}

function initialPhaseRows(points, start, interval, seconds = 90) {
  const end = Math.min(points.length, Math.max(start + 1, start + Math.round(seconds / Math.max(interval, 0.25))));
  return points.slice(Math.max(0, start), end);
}

function rowHasUsableThermal(point) {
  return (Number.isFinite(phaseThermalAverage(point, "egt")) && phaseThermalAverage(point, "egt") > 100)
    || (Number.isFinite(phaseThermalAverage(point, "cht")) && phaseThermalAverage(point, "cht") > 100);
}

function directPowerLooksAirborne(rows, smoothedPower, startIndex) {
  const rpmMedian = median(rows.map((point) => point.rpm).filter(Number.isFinite));
  const fuelMedian = median(rows.map((point) => point.fuelFlow).filter(Number.isFinite));
  const mapMedian = median(rows.map((point) => point.map).filter(Number.isFinite));
  const powerMedian = median(rows.map((_, offset) => smoothedPower[startIndex + offset]).filter(Number.isFinite));
  return (Number.isFinite(rpmMedian) && rpmMedian >= 1550)
    || (Number.isFinite(fuelMedian) && fuelMedian >= 4.5)
    || (Number.isFinite(mapMedian) && mapMedian >= 16)
    || (Number.isFinite(powerMedian) && powerMedian >= 0.48);
}

function initialThermalLooksInFlight(rows, interval) {
  const thermalRows = rows.filter(rowHasUsableThermal);
  if (!thermalRows.length) return false;
  const firstThermalOffset = rows.findIndex(rowHasUsableThermal);
  const firstThermalSeconds = firstThermalOffset >= 0 ? firstThermalOffset * interval : Infinity;
  const egtMedian = median(thermalRows.map((point) => phaseThermalAverage(point, "egt")).filter(Number.isFinite));
  const chtMedian = median(thermalRows.map((point) => phaseThermalAverage(point, "cht")).filter(Number.isFinite));
  const egtAlreadyHot = Number.isFinite(egtMedian) && egtMedian >= 1050;
  const chtAlreadyHot = Number.isFinite(chtMedian) && chtMedian >= 300;
  return firstThermalSeconds <= 30 && (egtAlreadyHot || chtAlreadyHot);
}

function initialLowPowerShare(rows, smoothedPower, startIndex) {
  let usable = 0;
  let low = 0;
  rows.forEach((point, offset) => {
    const direct = [
      Number.isFinite(point.rpm) ? point.rpm < 1300 : null,
      Number.isFinite(point.fuelFlow) ? point.fuelFlow < 3 : null,
      Number.isFinite(point.map) ? point.map < 14 : null,
      Number.isFinite(smoothedPower[startIndex + offset]) ? smoothedPower[startIndex + offset] < 0.28 : null,
    ].filter((value) => value !== null);
    if (!direct.length) return;
    usable += 1;
    if (direct.filter(Boolean).length >= Math.ceil(direct.length / 2)) low += 1;
  });
  return usable ? low / usable : 0;
}

function detectLogStartedInFlight(points, context) {
  const { basis, engineOn, engineStart, firstAirborne, interval, likelyGroundTest, smoothedPower } = context;
  if (likelyGroundTest || engineStart < 0) return null;
  const startGraceSamples = Math.max(2, Math.round(30 / Math.max(interval, 0.25)));
  if (engineStart > startGraceSamples) return null;

  const rows = initialPhaseRows(points, engineStart, interval, 90);
  if (rows.length < 3 || rows.filter((_, offset) => engineOn[engineStart + offset]).length < rows.length * 0.65) return null;

  const motionStartsAirborne = basis.hasMotionData && firstAirborne >= 0 && firstAirborne <= startGraceSamples;
  const engineLooksAirborne = directPowerLooksAirborne(rows, smoothedPower, engineStart)
    && initialThermalLooksInFlight(rows, interval)
    && initialLowPowerShare(rows, smoothedPower, engineStart) <= 0.2;
  const busMedian = median(rows.map((point) => point.buss).filter(Number.isFinite));
  const busAlreadyCharging = Number.isFinite(busMedian) && busMedian >= 13.2;
  if (!motionStartsAirborne && !(engineLooksAirborne && (busAlreadyCharging || !basis.hasMotionData))) return null;

  return {
    startIndex: engineStart,
    evidenceEndIndex: Math.min(points.length - 1, engineStart + Math.max(2, Math.round(180 / Math.max(interval, 0.25)))),
    motionStartsAirborne,
    busAlreadyCharging,
  };
}

function patternWorkCandidateFlags(labels, context) {
  const interval = context.interval ?? 1;
  const takeoffStart = Number.isFinite(context.takeoffStart) ? context.takeoffStart : context.engineStart ?? 0;
  const skipInitialSamples = Math.max(0, Math.round(Math.max(context.takeoffSeconds ?? 75, 75) / Math.max(interval, 0.25)));
  const firstCandidateIndex = Math.max(0, takeoffStart + skipInitialSamples);
  return labels.map((label, index) => {
    if (index < firstCandidateIndex) return false;
    if (!context.engineOn?.[index]) return false;
    return patternWorkCandidatePhases.has(label);
  });
}

function patternWorkGapSummary(proxyValues, candidateFlags, points, start, end, lowThreshold) {
  if (end <= start) return null;
  const values = [];
  let lowCount = 0;
  for (let index = start; index < end; index += 1) {
    const value = proxyValues[index];
    if (!candidateFlags[index] || !Number.isFinite(value)) continue;
    values.push(value);
    if (value <= lowThreshold) lowCount += 1;
  }
  if (values.length < Math.max(2, (end - start) * 0.25)) return null;
  const duration = Math.max(0, (points[end - 1]?.elapsedSeconds ?? end - 1) - (points[start]?.elapsedSeconds ?? start));
  const medianProxy = median(values);
  const lowShare = lowCount / Math.max(values.length, 1);
  if (duration < 25 || duration > 600) return null;
  if (lowShare < 0.18 && !(Number.isFinite(medianProxy) && medianProxy <= lowThreshold + 0.06)) return null;
  return { start, end, duration, medianProxy, lowShare };
}

function evaluatePatternWorkSequence(points, sequence, gaps, proxyValues, egtValues, chtValues, interval, directPowerAvailable) {
  if (sequence.length < 3 || gaps.length < sequence.length - 1) return null;
  const centers = sequence.map((run) => phaseRunCenterSeconds(points, run));
  const intervals = centers.slice(1).map((center, index) => center - centers[index]).filter(Number.isFinite);
  const meanInterval = averageValues(intervals);
  if (!Number.isFinite(meanInterval) || meanInterval < 90 || meanInterval > 600) return null;
  const intervalSpread = rangeOf(intervals) ?? 0;
  const regularity = meanInterval > 0 ? Math.max(0, 1 - intervalSpread / Math.max(meanInterval * 1.7, 1)) : 0;

  const highProxy = sequence.map((run) => phaseRunMedian(proxyValues, run)).filter(Number.isFinite);
  const lowProxy = gaps.map((gap) => gap.medianProxy).filter(Number.isFinite);
  if (highProxy.length < sequence.length || lowProxy.length < sequence.length - 1) return null;
  const proxySwing = median(gaps.map((gap, index) => {
    const before = highProxy[index];
    const after = highProxy[index + 1];
    return Math.max(before ?? -Infinity, after ?? -Infinity) - gap.medianProxy;
  }).filter(Number.isFinite));
  if (!Number.isFinite(proxySwing) || proxySwing < (directPowerAvailable ? 0.12 : 0.18)) return null;

  const start = sequence[0].start;
  const end = sequence.at(-1).end;
  const egtRange = (phaseRangeQuantile(egtValues, start, end, 0.9) ?? 0) - (phaseRangeQuantile(egtValues, start, end, 0.1) ?? 0);
  const chtRange = (phaseRangeQuantile(chtValues, start, end, 0.9) ?? 0) - (phaseRangeQuantile(chtValues, start, end, 0.1) ?? 0);
  const thermalPad = Math.max(1, Math.round(45 / Math.max(interval, 0.25)));
  const confirmedGaps = gaps.filter((gap, index) => {
    const beforeHigh = sequence[index];
    const afterHigh = sequence[index + 1];
    const beforeChtPeak = phaseRangeQuantile(chtValues, beforeHigh.start, beforeHigh.end + thermalPad, 0.75);
    const afterChtPeak = phaseRangeQuantile(chtValues, afterHigh.start, afterHigh.end + thermalPad, 0.75);
    const lowCht = phaseRangeQuantile(chtValues, gap.start, gap.end, 0.25);
    const beforeEgt = phaseRunMedian(egtValues, beforeHigh);
    const afterEgt = phaseRunMedian(egtValues, afterHigh);
    const lowEgt = phaseRangeQuantile(egtValues, gap.start, gap.end, 0.5);
    const chtSwing = Math.max(beforeChtPeak ?? -Infinity, afterChtPeak ?? -Infinity) - (lowCht ?? Infinity);
    const egtSwing = Math.max(beforeEgt ?? -Infinity, afterEgt ?? -Infinity) - (lowEgt ?? Infinity);
    return (Number.isFinite(chtSwing) && chtSwing >= 9)
      || (Number.isFinite(egtSwing) && egtSwing >= 28 && Number.isFinite(chtRange) && chtRange >= 18);
  }).length;
  const thermalConfirmed = confirmedGaps >= Math.min(2, gaps.length)
    || (directPowerAvailable && Number.isFinite(chtRange) && chtRange >= 24 && Number.isFinite(egtRange) && egtRange >= 32);
  if (!thermalConfirmed) return null;

  const score = clampScore(
    45
    + Math.min(24, (sequence.length - 3) * 7)
    + Math.min(18, proxySwing * 78)
    + Math.min(18, Math.max(0, chtRange) * 0.42)
    + Math.min(10, Math.max(0, egtRange) * 0.12)
    + regularity * 12
  );
  if (score < 68) return null;
  return {
    startIndex: start,
    endIndex: end,
    startTime: points[start]?.elapsedSeconds ?? start,
    endTime: points[Math.max(start, end - 1)]?.elapsedSeconds ?? Math.max(start, end - 1),
    cycleCount: sequence.length,
    score,
    proxySwing,
    chtRange,
    egtRange,
    regularity,
  };
}

function detectPatternWorkWindows(points, labels, context) {
  const interval = context.interval ?? sampleIntervalSeconds(points);
  if (points.length < Math.max(45, Math.round(540 / Math.max(interval, 0.25)))) return [];
  const candidateFlags = patternWorkCandidateFlags(labels, context);
  const proxyValues = patternWorkPowerProxy(points, context);
  const candidateValues = proxyValues.filter((value, index) => candidateFlags[index] && Number.isFinite(value));
  if (candidateValues.length < Math.max(36, Math.round(360 / Math.max(interval, 0.25)))) return [];

  const lowThreshold = quantile(candidateValues, 0.36);
  const highThreshold = quantile(candidateValues, 0.68);
  if (!Number.isFinite(lowThreshold) || !Number.isFinite(highThreshold) || highThreshold - lowThreshold < 0.12) return [];

  const minHighSamples = Math.max(3, Math.round(24 / Math.max(interval, 0.25)));
  const closeGapSamples = Math.max(1, Math.round(18 / Math.max(interval, 0.25)));
  const highFlags = cleanPhasePatternFlags(
    proxyValues.map((value, index) => candidateFlags[index] && Number.isFinite(value) && value >= highThreshold),
    closeGapSamples,
    minHighSamples,
  );
  const highRuns = flagRuns(highFlags)
    .filter((run) => run.value)
    .filter((run) => {
      const duration = runDurationSeconds(points, run);
      return duration >= 24 && duration <= 480;
    });
  if (highRuns.length < 3) return [];

  const directPowerAvailable = (context.basis?.powerChannels?.length ?? 0) > 0;
  const egtValues = points.map((point) => phaseThermalAverage(point, "egt"));
  const chtValues = points.map((point) => phaseThermalAverage(point, "cht"));
  const windows = [];
  let sequence = [];
  let gaps = [];
  const finishSequence = () => {
    const window = evaluatePatternWorkSequence(points, sequence, gaps, proxyValues, egtValues, chtValues, interval, directPowerAvailable);
    if (window) windows.push(window);
  };

  highRuns.forEach((run) => {
    if (!sequence.length) {
      sequence = [run];
      gaps = [];
      return;
    }
    const previous = sequence.at(-1);
    const centerSpacing = phaseRunCenterSeconds(points, run) - phaseRunCenterSeconds(points, previous);
    const gap = patternWorkGapSummary(proxyValues, candidateFlags, points, previous.end, run.start, lowThreshold);
    if (Number.isFinite(centerSpacing) && centerSpacing >= 90 && centerSpacing <= 600 && gap) {
      sequence.push(run);
      gaps.push(gap);
      return;
    }
    finishSequence();
    sequence = [run];
    gaps = [];
  });
  finishSequence();

  return windows.sort((a, b) => a.startTime - b.startTime);
}

function applyPatternWorkDetection(points, labels, context) {
  const windows = detectPatternWorkWindows(points, labels, context);
  if (!windows.length) return { points, labels, windows };
  const nextLabels = [...labels];
  const details = Array(points.length).fill(null);
  windows.forEach((window) => {
    for (let index = window.startIndex; index < window.endIndex; index += 1) {
      if (!patternWorkCandidatePhases.has(nextLabels[index])) continue;
      nextLabels[index] = patternWorkPhaseName;
      details[index] = window;
    }
  });
  return {
    labels: nextLabels,
    windows,
    points: points.map((point, index) => {
      const detail = details[index];
      if (!detail) return point;
      return {
        ...point,
        patternWorkCycleCount: detail.cycleCount,
        patternWorkScore: detail.score,
      };
    }),
  };
}

export function assignFlightPhases(points) {
  if (!points.length) return points;
  const interval = sampleIntervalSeconds(points);
  const runSegments = phaseEngineRunSegments(points, phaseEngineRunFlags(points), interval);
  if (runSegments.length > 1) return assignMultiRunFlightPhases(points, runSegments, interval);
  return assignSingleFlightPhases(points);
}

export function assignSingleFlightPhases(points) {
  if (!points.length) return points;

  const interval = sampleIntervalSeconds(points);
  const smoothRadius = Math.max(1, Math.round(18 / Math.max(interval, 0.25)));
  const ignoreGpsPlaceholder = phaseGpsMotionLooksPlaceholder(points);
  const speedKey = bestPhaseSpeedKey(points, ignoreGpsPlaceholder ? new Set(["groundSpeed"]) : new Set());
  const speedValues = speedKey ? rollingAverage(points.map((point) => point[speedKey]), smoothRadius) : [];
  const altitudeValues = rollingAverage(phaseAltitudeInput(points, speedKey, { ignoreGpsPlaceholder }), smoothRadius);
  const verticalSpeeds = deriveVerticalSpeeds(points, altitudeValues);
  const groundContactValues = phaseGroundContactValues(points);
  const basis = phaseDataBasis(points, speedKey, groundContactValues, { ignoreGpsPlaceholder });
  const hasGroundContactData = basis.hasGroundContactData;
  const scales = {
    fuelFlow: phaseChannelScale(points, "fuelFlow"),
    rpm: phaseChannelScale(points, "rpm"),
    map: phaseChannelScale(points, "map"),
    egt: phaseChannelScale(points, "egt"),
    cht: phaseChannelScale(points, "cht"),
  };

  const powerScores = points.map((point) => {
    const signals = [
      normalizedSignal(point.fuelFlow, scales.fuelFlow.low, scales.fuelFlow.high),
      normalizedSignal(point.rpm, scales.rpm.low, scales.rpm.high),
      normalizedSignal(point.map, scales.map.low, scales.map.high),
    ].filter(Number.isFinite);
    if (signals.length) return averageValues(signals);
    return averageValues([
      normalizedSignal(point.egt, scales.egt.low, scales.egt.high),
      normalizedSignal(point.cht, scales.cht.low, scales.cht.high),
    ]);
  });
  const smoothedPower = rollingAverage(powerScores, smoothRadius);
  const speedExtent = extentFromValues(speedValues);
  const altitudeExtent = extentFromValues(altitudeValues);
  const edgeAltitudes = [
    ...altitudeValues.slice(0, Math.max(3, Math.round(points.length * 0.08))),
    ...altitudeValues.slice(Math.max(0, points.length - Math.max(3, Math.round(points.length * 0.08)))),
  ].filter(Number.isFinite);
  const groundAltitude = edgeAltitudes.length ? quantile(edgeAltitudes, 0.18) : altitudeExtent?.min;
  const hasMotionData = basis.hasMotionData;
  const engineOn = phaseEngineRunFlags(points, smoothedPower);
  const engineStart = engineOn.findIndex(Boolean);
  const engineEnd = engineOn.length - 1 - [...engineOn].reverse().findIndex(Boolean);
  if (engineStart < 0) return points.map((point) => ({
    ...point,
    phase: point.phase || null,
    phaseConfidence: "Low",
    phaseEvidence: "No engine-run state detected",
    phaseSource: basis.sourceLabel,
  }));

  const speedAirborneThreshold = speedExtent ? Math.max(35, speedExtent.max * 0.38) : Infinity;
  const speedAirborneMinimum = speedExtent ? Math.max(20, speedAirborneThreshold * 0.45) : Infinity;
  const airborne = points.map((_, index) => {
    const groundContact = groundContactValues[index];
    const groundContactGround = hasGroundContactData && groundContact === true;
    const groundContactAirborne = hasGroundContactData && groundContact === false;
    const speedFlying = Number.isFinite(speedValues[index]) && speedValues[index] >= speedAirborneThreshold;
    const speedCorroboratesAltitude = !speedKey || (Number.isFinite(speedValues[index]) && speedValues[index] >= speedAirborneMinimum);
    const altitudeFlying = Number.isFinite(altitudeValues[index])
      && Number.isFinite(groundAltitude)
      && altitudeValues[index] - groundAltitude > 180
      && speedCorroboratesAltitude;
    return engineOn[index] && !groundContactGround && (groundContactAirborne || speedFlying || altitudeFlying);
  });
  const firstAirborne = sustainedIndex(airborne, engineStart, interval, 25);
  const reverseAirborneStart = sustainedIndex([...airborne].reverse(), 0, interval, 25);
  const lastAirborne = reverseAirborneStart >= 0 ? airborne.length - 1 - reverseAirborneStart : -1;
  const highPowerFlags = smoothedPower.map((score, index) => engineOn[index] && (score ?? 0) >= 0.68);
  const lowPowerFlags = smoothedPower.map((score, index) => engineOn[index] && (score ?? 0) <= 0.32);
  const firstHighPower = sustainedIndex(highPowerFlags, engineStart, interval, 25);
  const highPowerRunCount = sustainedRunCount(highPowerFlags, interval, 20);
  const lowPowerRunCount = sustainedRunCount(lowPowerFlags, interval, 20);
  const engineDuration = Math.max(1, (points[engineEnd]?.elapsedSeconds ?? engineEnd) - (points[engineStart]?.elapsedSeconds ?? engineStart));
  const hasAirborneEvidence = firstAirborne >= 0 && lastAirborne >= firstAirborne;
  const repeatedGroundPower = highPowerRunCount >= 2 && lowPowerRunCount >= 1;
  const altitudeRange = altitudeExtent ? altitudeExtent.max - altitudeExtent.min : null;
  const verticalSpeedExtent = extentFromValues(verticalSpeeds);
  const verticalSpeedRange = verticalSpeedExtent ? Math.max(Math.abs(verticalSpeedExtent.min), Math.abs(verticalSpeedExtent.max)) : null;
  const speedShowsGround = speedExtent && speedExtent.max < 25;
  const altitudeShowsGround = basis.hasAltitude
    && Number.isFinite(altitudeRange)
    && altitudeRange < 150
    && (!basis.hasVerticalSpeed || !Number.isFinite(verticalSpeedRange) || verticalSpeedRange < 180);
  const motionShowsGround = Boolean(speedShowsGround || (!speedKey && altitudeShowsGround));
  const contactShowsGround = hasGroundContactData
    && groundContactValues.filter((value) => value === false).length <= Math.max(1, groundContactValues.filter((value) => typeof value === "boolean").length * 0.05);
  const shortEngineOnlyRun = !basis.hasMotionData && engineDuration <= 600;
  const likelyGroundTest = !hasAirborneEvidence
    && (
      shortEngineOnlyRun
      || contactShowsGround
      || (motionShowsGround && (repeatedGroundPower || engineDuration <= 1800 || highPowerRunCount >= 1))
    );
  const logStartedInFlight = detectLogStartedInFlight(points, {
    basis,
    engineOn,
    engineStart,
    firstAirborne,
    interval,
    likelyGroundTest,
    smoothedPower,
  });
  const secondsBetween = (fromIndex, toIndex) => {
    const from = points[fromIndex]?.elapsedSeconds ?? fromIndex;
    const to = points[toIndex]?.elapsedSeconds ?? toIndex;
    const delta = Math.abs(to - from);
    return Number.isFinite(delta) ? delta : Infinity;
  };
  const detectedTakeoffStart = firstAirborne >= 0 && firstHighPower >= 0
    ? secondsBetween(firstAirborne, firstHighPower) <= 180
      ? Math.min(firstAirborne, firstHighPower)
      : firstHighPower
    : firstAirborne >= 0
      ? firstAirborne
      : firstHighPower >= 0
        ? firstHighPower
        : Math.min(points.length - 1, engineStart + Math.round(points.length * 0.18));
  const takeoffStart = logStartedInFlight ? engineStart : detectedTakeoffStart;
  const fallbackAirborneStart = Math.max(takeoffStart, engineStart);
  const fallbackAirborneEnd = engineEnd >= 0 ? engineEnd : points.length - 1;
  const airborneStart = firstAirborne >= 0 ? firstAirborne : fallbackAirborneStart;
  const airborneEnd = firstAirborne >= 0 && lastAirborne >= firstAirborne ? lastAirborne : fallbackAirborneEnd;
  const airborneDuration = Math.max(1, points[airborneEnd]?.elapsedSeconds - points[airborneStart]?.elapsedSeconds || airborneEnd - airborneStart);
  const takeoffSeconds = logStartedInFlight ? 0 : hasMotionData ? 75 : Math.min(180, Math.max(60, airborneDuration * 0.12));
  const climbSeconds = Math.min(airborneDuration * 0.35, Math.max(300, airborneDuration * 0.18));
  const descentStartFallback = airborneStart + Math.max(1, Math.round((airborneEnd - airborneStart) * 0.72));

  const labels = points.map((point, index) => {
    if (!engineOn[index]) return index < engineStart ? "Startup" : "Shutdown";
    if (likelyGroundTest) {
      const secondsFromStart = (point.elapsedSeconds ?? index) - (points[engineStart]?.elapsedSeconds ?? engineStart);
      const speed = speedValues[index];
      const lowMotion = !Number.isFinite(speed) || speed < 5;
      const rpm = point.rpm;
      const fuelFlow = point.fuelFlow;
      const power = smoothedPower[index] ?? 0;
      const warmupPower = power < 0.28
        || (Number.isFinite(rpm) && rpm < 1250 && (!Number.isFinite(fuelFlow) || fuelFlow < 3));
      if (shortEngineOnlyRun) {
        if (secondsFromStart < 180 && lowMotion && warmupPower) return "Startup";
        return "Runup";
      }
      if (secondsFromStart < 180 && lowMotion && warmupPower) return "Startup";
      if (power > 0.42 || (Number.isFinite(rpm) && rpm > 1500) || (Number.isFinite(fuelFlow) && fuelFlow > 3.2)) return "Ground Run";
      if (!basis.hasMotionData) return "Ground Run";
      return "Taxi";
    }
    if (!logStartedInFlight && index < takeoffStart) {
      const secondsFromStart = (point.elapsedSeconds ?? index) - (points[engineStart]?.elapsedSeconds ?? engineStart);
      const speed = speedValues[index];
      const lowMotion = !Number.isFinite(speed) || speed < 5;
      const rpm = point.rpm;
      const fuelFlow = point.fuelFlow;
      const warmupPower = (smoothedPower[index] ?? 0) < 0.28
        || (Number.isFinite(rpm) && rpm < 1250 && (!Number.isFinite(fuelFlow) || fuelFlow < 3));
      if (secondsFromStart < 180 && lowMotion && warmupPower) return "Startup";
      if ((smoothedPower[index] ?? 0) > 0.5 || (Number.isFinite(rpm) && rpm > 1600) || (Number.isFinite(fuelFlow) && fuelFlow > 3.2)) return "Runup";
      return "Taxi";
    }

    const secondsSinceTakeoff = (point.elapsedSeconds ?? index) - (points[takeoffStart]?.elapsedSeconds ?? takeoffStart);
    if (!logStartedInFlight && secondsSinceTakeoff <= takeoffSeconds) return "Takeoff";

    const airborneRatio = (index - airborneStart) / Math.max(1, airborneEnd - airborneStart);
    const vs = verticalSpeeds[index];
    const power = smoothedPower[index] ?? 0;
    const climbing = Number.isFinite(vs) ? vs > 180 : airborneRatio < 0.35 && power > 0.45;
    const descending = Number.isFinite(vs) ? vs < -180 : index >= descentStartFallback && power < 0.48;

    if (index > airborneEnd || (airborneRatio > 0.88 && power < 0.45)) return "Landing";
    if (climbing && secondsSinceTakeoff <= climbSeconds) return "Climb";
    if (descending || index >= descentStartFallback && power < 0.5) return "Descent";
    if (climbing && power > 0.58 && airborneRatio < 0.55) return "Climb";
    return "Cruise";
  });

  const patternWork = applyPatternWorkDetection(points, labels, {
    basis,
    interval,
    smoothedPower,
    engineOn,
    engineStart,
    takeoffStart,
    takeoffSeconds,
  });
  const phased = smoothPhaseLabels(patternWork.points.map((point, index) => ({ ...point, phase: patternWork.labels[index] })), interval);
  return addPhaseMetadata(phased, {
    basis,
    speedKey,
    speedValues,
    altitudeValues,
    verticalSpeeds,
    smoothedPower,
    firstAirborne,
    firstHighPower,
    likelyGroundTest,
    logStartedInFlight: Boolean(logStartedInFlight),
    logStartedInFlightEvidenceEndIndex: logStartedInFlight?.evidenceEndIndex,
    patternWorkWindows: patternWork.windows,
    groundContactValues,
  });
}

function smoothPhaseLabels(points, sampleInterval) {
  const minSamples = Math.max(2, Math.round(20 / Math.max(sampleInterval, 0.25)));
  const smoothed = points.map((point) => ({ ...point }));
  const phaseRuns = () => {
    const runs = [];
    let start = 0;
    while (start < smoothed.length) {
      let end = start + 1;
      while (end < smoothed.length && smoothed[end].phase === smoothed[start].phase) end += 1;
      runs.push({ start, end, phase: smoothed[start].phase, count: end - start });
      start = end;
    }
    return runs;
  };

  const replaceRun = (run, phase) => {
    for (let index = run.start; index < run.end; index += 1) smoothed[index].phase = phase;
  };

  for (const run of phaseRuns()) {
    if (run.count >= minSamples || run.start === 0 || run.end >= smoothed.length) continue;
    const previous = smoothed[run.start - 1].phase;
    const next = smoothed[run.end].phase;
    if (previous === next) {
      replaceRun(run, previous);
      continue;
    }
    if (groundPhaseNames.has(run.phase) && groundPhaseNames.has(previous) && groundPhaseNames.has(next)) {
      replaceRun(run, previous === "Runup" || next !== "Runup" ? previous : next);
    }
  }
  return smoothed;
}
